import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatUsd, type StepDiff } from "@veyso/core";
import { AppShell } from "../components/AppShell.js";
import { ErrorNote, Eyebrow, STEP_LABELS, Spinner } from "../components/ui.js";
import { StepDiffView } from "../steps/StepDiffView.js";
import { StepOutputView } from "../steps/StepOutputView.js";
import { SNAPSHOTS_KEPT, formatDuration, formatKey, formatValue, formatWhen, groupRunsByJob } from "../steps/format.js";
import { api, type StepRunSummary } from "../api.js";

const STATUS_TONE: Record<string, string> = {
  completed: "text-signal-ok",
  failed: "text-signal-bad",
  skipped: "text-ink-400",
  running: "text-ember-400",
};

/** The steps the pipeline runs that are not project-level pipeline steps, so
 *  `STEP_LABELS` does not know them. */
/**
 * What each job was, in the words of the person who caused it.
 *
 * The heading used to read "VOICE" or "SCRIPT" — our job types, not anything
 * the reader asked for. Four such headings above one ingest read as a jumble
 * rather than as four things they did.
 */
const JOB_LABELS: Record<string, string> = {
  process: "Processing the upload",
  script: "AI rewrite",
  voice: "Generating narration",
  autocut: "Auto-cut",
  proxy: "Preparing the preview",
  render: "Rendering the video",
  vision: "Re-running the visual analysis",
};

const jobLabel = (type: string) => JOB_LABELS[type] ?? type;

const EXTRA_LABELS: Record<string, string> = {
  "visuals-analysed": "Visual analysis",
  "frames-judged": "Frame analysis",
  "cut-planned": "Cut plan",
  "proxy-built": "Proxy",
  bundled: "Bundle",
  rendered: "Render",
};

// `STEP_LABELS` is keyed by `PipelineStep`; a recorded step is whatever string
// the worker wrote, so it is read as an open map and falls back to itself.
const known: Record<string, string | undefined> = { ...STEP_LABELS, ...EXTRA_LABELS };
const label = (step: string): string => known[step] ?? step;

/**
 * The previous run this run's diff is measured against.
 *
 * Mirrors the API's own lookup — same step, completed, most recent one that
 * started earlier — because the diff endpoint answers 404 for several different
 * reasons and only the caller, holding the whole list, can tell them apart.
 * `runs` arrives newest-first, so the first match is the nearest earlier run.
 */
const previousRunOf = (runs: StepRunSummary[], run: StepRunSummary): StepRunSummary | null =>
  runs.find(
    (candidate) =>
      candidate.step === run.step &&
      candidate.status === "completed" &&
      new Date(candidate.startedAt).getTime() < new Date(run.startedAt).getTime(),
  ) ?? null;

/** The steps `diffStepSnapshots` has a differ for. Everything else records
 *  history without ever being compared, and saying so is kinder than implying
 *  its snapshots were the wrong shape. */
const DIFFABLE = new Set(["script-generated", "transcribed", "actions-detected", "timeline-built", "cut-planned"]);

/**
 * Why there is no diff to show.
 *
 * An empty diff and a missing one look identical on screen and mean opposite
 * things — "nothing changed" versus "we no longer know" — so every path that
 * ends without a diff says which it is.
 */
const explainNoDiff = (runs: StepRunSummary[], run: StepRunSummary): string => {
  if (run.status === "running") return "This run has not finished yet.";
  if (run.status === "skipped") return "This step was skipped, so there is nothing to compare.";

  const previous = previousRunOf(runs, run);
  if (!previous) return "No earlier run of this step to compare against.";

  // Pruning always leaves the newest snapshots in place, so a step with no
  // surviving snapshot anywhere is a step that never kept one — a render, say —
  // rather than one whose history has aged out.
  if (!runs.some((candidate) => candidate.step === run.step && candidate.hasSnapshot)) {
    return "This step does not keep a copy of what it produced, so there is nothing to compare.";
  }
  if (!run.hasSnapshot) {
    return `This run's snapshot has been pruned — only the last ${SNAPSHOTS_KEPT} runs of a step keep one — so the summary above is all that remains of it.`;
  }
  if (!previous.hasSnapshot) {
    return "The earlier run's snapshot has been pruned, so there is nothing left to compare this one against.";
  }
  if (!DIFFABLE.has(run.step)) return "This step is recorded but not compared between runs.";
  return "The two snapshots are not in a shape this build can compare.";
};

/**
 * What the pipeline actually did, run by run.
 *
 * The project's status can only say which steps have output; it cannot say when
 * a step ran, how long it took, or what changed when it ran again. This page is
 * the record — and on a bad day it is the only place a failed job's error
 * survives, since the panes that start jobs lose theirs when they unmount.
 */
export const Steps: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const [runs, setRuns] = useState<StepRunSummary[] | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [diff, setDiff] = useState<StepDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Re-running is deliberate and one at a time, so a note beside the button is
  // enough — a toast would outlive the page it belongs to.
  const [rerunning, setRerunning] = useState(false);
  const [rerunNote, setRerunNote] = useState<string | null>(null);
  // What the step produced, fetched separately because it is the large field —
  // the list deliberately never carries it.
  const [output, setOutput] = useState<unknown>(null);
  const [outputLoading, setOutputLoading] = useState(false);

  const run = runs?.find((entry) => entry.id === selected) ?? null;
  const live = runs?.some((entry) => entry.status === "running") ?? false;

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const result = await api.steps(id);
        if (cancelled) return;
        setRuns(result.runs);
        // Keep whatever the reader is looking at across a refresh; only fall
        // back to the newest run when the selection is gone or never made.
        setSelected((current) =>
          current && result.runs.some((entry) => entry.id === current) ? current : (result.runs[0]?.id ?? null),
        );
        setError(null);
      } catch (cause) {
        if (!cancelled) setError((cause as Error).message);
      }
    };

    void load();

    // History is static once nothing is running, so the timer exists only for
    // the case where the reader opened this page to watch a job finish.
    if (!live) {
      return () => {
        cancelled = true;
      };
    }
    const timer = setInterval(() => void load(), 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [id, live]);

  // Only for the breadcrumb, so a failure to read it must not take the page down.
  useEffect(() => {
    let cancelled = false;
    void api
      .getProject(id)
      .then((project) => {
        if (!cancelled) setName(project.name);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [id]);

  // The diff is `diffStepSnapshots(previous.snapshot, run.snapshot)`, so a run
  // with no snapshot of its own can only ever answer 404 — failed, skipped,
  // still running, pruned, or a step that never kept one. Not asking keeps the
  // console clear of 404s that are not faults.
  const comparable = run?.hasSnapshot ?? false;

  useEffect(() => {
    if (!selected || !comparable) {
      setDiff(null);
      setDiffLoading(false);
      return;
    }

    let cancelled = false;
    setDiff(null);
    setDiffLoading(true);
    void api
      .stepDiff(id, selected)
      .then((result) => {
        if (cancelled) return;
        setDiff(result);
        setDiffLoading(false);
      })
      // 404 is the ordinary answer for a first run, a pruned snapshot or a step
      // with nothing comparable. `explainNoDiff` says which; none is an error.
      .catch(() => {
        if (cancelled) return;
        setDiff(null);
        setDiffLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, selected, comparable]);

  useEffect(() => {
    if (!selected || !comparable) {
      setOutput(null);
      setOutputLoading(false);
      return;
    }

    let cancelled = false;
    setOutput(null);
    setOutputLoading(true);
    void api
      .stepOutput(id, selected)
      .then((result) => {
        if (cancelled) return;
        setOutput(result.snapshot);
        setOutputLoading(false);
      })
      // 404 means the snapshot aged out. Ordinary for an old run, not a fault.
      .catch(() => {
        if (cancelled) return;
        setOutput(null);
        setOutputLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, selected, comparable]);

  const failures = runs?.filter((entry) => entry.status === "failed").length ?? 0;

  return (
    <AppShell
      live={live}
      breadcrumb={
        <>
          <Link to="/dashboard" className="shrink-0 text-ink-400 transition-colors hover:text-ink-200">
            Projects
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <Link
            to={`/projects/${id}/editor`}
            className="min-w-0 truncate text-ink-400 transition-colors hover:text-ink-200"
            title={name ?? undefined}
          >
            {name ?? "Project"}
          </Link>
          <span className="shrink-0 text-ink-400">/</span>
          <span className="shrink-0 font-medium text-ink-100">Steps</span>
        </>
      }
      actions={
        <Link to={`/projects/${id}/editor`} className="btn btn-ghost btn-sm">
          Back to editor
        </Link>
      }
    >
      <div className="flex flex-col gap-5">
        <div>
          <h1 className="text-[26px] font-semibold text-ink-100">Steps</h1>
          <p className="mt-1 text-[13px] text-ink-300">
            Grouped by the job that ran them, newest first. Each heading is one thing that happened to this project — an
            upload, a rewrite, a render — and the steps beneath it are what that job did. Every job rebuilds the
            timeline, which is why that step appears in most of them.
            {failures > 0 ? (
              <span className="ml-1.5 font-medium text-signal-bad">
                {failures} failed {failures === 1 ? "run" : "runs"}.
              </span>
            ) : null}
          </p>
        </div>

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {/* A spinner under an error banner claims the page is still trying when
            the load has already given up. */}
        {!runs ? (
          error ? null : (
            <Spinner />
          )
        ) : runs.length === 0 ? (
          <p className="text-[13px] text-ink-300">
            No steps recorded yet. History begins with the next job this project runs.
          </p>
        ) : (
          <div className="flex gap-5">
            <div className="flex max-h-[70vh] w-[280px] shrink-0 flex-col gap-4 overflow-y-auto pr-1">
              {groupRunsByJob(runs).map((group) => (
                <section key={group.jobId} className="flex flex-col gap-1.5">
                  {/* The job is the event; its steps are what the event did. */}
                  <div className="flex items-baseline justify-between gap-2 px-0.5">
                    <Eyebrow>{jobLabel(group.jobType)}</Eyebrow>
                    <span className="tnum shrink-0 text-[10px] text-ink-400">{formatWhen(group.latestAt)}</span>
                  </div>

                  <ul className="flex flex-col gap-1">
                    {group.runs.map((entry) => (
                      <li key={entry.id}>
                        <button
                          onClick={() => setSelected(entry.id)}
                          aria-current={entry.id === selected}
                          className={`w-full rounded-[6px] border p-2 text-left transition-colors ${
                            entry.id === selected
                              ? "border-scope-400 bg-bay-850"
                              : entry.status === "failed"
                                ? "border-signal-bad/30 bg-signal-bad/5 hover:border-signal-bad/50"
                                : "border-[var(--edge)] hover:border-[var(--edge-strong)]"
                          }`}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate text-[12px] text-ink-100">{label(entry.step)}</span>
                            <span
                              className={`shrink-0 text-[10px] font-medium ${
                                STATUS_TONE[entry.status] ?? "text-ink-400"
                              }`}
                            >
                              {entry.status}
                            </span>
                          </div>
                          <div className="mt-0.5 flex gap-2 text-[10px] text-ink-400">
                            <span className="tnum">{formatWhen(entry.startedAt)}</span>
                            <span className="tnum">{formatDuration(entry.durationMs)}</span>
                            {entry.costUsd > 0 ? <span className="tnum">{formatUsd(entry.costUsd)}</span> : null}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            <div className="min-w-0 flex-1">
              {!run ? null : (
                <div className="flex flex-col gap-5">
                  {/* The failure comes first and stays in ink, not in a footnote.
                      Once the pane that started the job is gone this is the only
                      surviving copy of what the provider said. */}
                  {run.status === "failed" ? (
                    <section className="flex flex-col gap-2">
                      <h2 className="text-[13px] font-semibold text-signal-bad">
                        {label(run.step)} failed
                        {run.attempt > 1 ? ` on attempt ${run.attempt}` : ""}
                      </h2>
                      <ErrorNote>
                        <span className="block whitespace-pre-wrap break-words leading-relaxed">
                          {run.error ?? "The worker recorded no message for this failure."}
                        </span>
                      </ErrorNote>

                      {/* Only this step. Every other failure here is either
                          fatal to its job, which the worker already retries, or
                          fixed by changing something rather than asking again —
                          a busy model is the one worth simply re-trying. */}
                      {run.step === "visuals-analysed" ? (
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={rerunning}
                            onClick={() => {
                              setRerunning(true);
                              setRerunNote(null);
                              void api
                                .analyse(id)
                                .then(() => setRerunNote("Queued. It will appear here as a new run."))
                                .catch((cause: Error) => setRerunNote(cause.message))
                                .finally(() => setRerunning(false));
                            }}
                          >
                            {rerunning ? "Queueing…" : "Run it again"}
                          </button>
                          {rerunNote ? <span className="text-[11px] text-ink-400">{rerunNote}</span> : null}
                        </div>
                      ) : null}
                    </section>
                  ) : null}

                  <section className="panel p-5">
                    <div className="flex items-baseline justify-between gap-3">
                      {/* `attempt` is the job's attempt count, which the queue
                          raises to 1 as it claims the job — so anything above 1
                          is a retry, and a first attempt is not worth saying. */}
                      <Eyebrow>
                        {label(run.step)} · {run.jobType}
                        {run.attempt > 1 ? ` · attempt ${run.attempt}` : ""}
                      </Eyebrow>
                      <span className={`text-[11px] font-medium ${STATUS_TONE[run.status] ?? "text-ink-400"}`}>
                        {run.status}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-ink-400">
                      <span>
                        Started <span className="tnum text-ink-200">{formatWhen(run.startedAt)}</span>
                      </span>
                      <span>
                        Took <span className="tnum text-ink-200">{formatDuration(run.durationMs)}</span>
                      </span>
                      <span>
                        Cost <span className="tnum text-ink-200">{formatUsd(run.costUsd)}</span>
                      </span>
                    </div>

                    {Object.keys(run.summary ?? {}).length === 0 ? (
                      <p className="mt-3 text-[12px] text-ink-400">This step recorded no summary.</p>
                    ) : (
                      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
                        {Object.entries(run.summary).map(([key, value]) => (
                          <React.Fragment key={key}>
                            <dt className="text-[11px] text-ink-400">{formatKey(key)}</dt>
                            <dd className="tnum min-w-0 break-words text-[11px] text-ink-100">
                              {formatValue(key, value)}
                            </dd>
                          </React.Fragment>
                        ))}
                      </dl>
                    )}
                  </section>

                  {/* What it produced, before what changed. The output is the
                      question people arrive with; the diff is the follow-up. */}
                  {run.status === "failed" || !run.hasSnapshot ? null : outputLoading ? (
                    <section className="panel p-5">
                      <Spinner />
                    </section>
                  ) : output ? (
                    <StepOutputView step={run.step} output={output} />
                  ) : (
                    <section className="panel p-5">
                      <Eyebrow>What it produced</Eyebrow>
                      <p className="mt-3 text-[12px] leading-relaxed text-ink-400">
                        This run kept a copy of its output, but it has since aged out — only the {SNAPSHOTS_KEPT} most
                        recent runs of a step keep one.
                      </p>
                    </section>
                  )}

                  {run.status === "failed" ? null : (
                    <section className="panel p-5">
                      <Eyebrow>Against the previous run</Eyebrow>
                      <div className="mt-3">
                        {diffLoading ? (
                          <Spinner />
                        ) : diff ? (
                          <StepDiffView diff={diff} />
                        ) : (
                          <p className="text-[12px] leading-relaxed text-ink-400">{explainNoDiff(runs, run)}</p>
                        )}
                      </div>
                    </section>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
};
